/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/apiSettings.scss';

import React, {createRef, MouseEventHandler} from 'react';

import {useTranslation} from 'react-i18next';

import {Icon} from '@iconify/react';

import optionsHorizontal from '@iconify/icons-mi/options-horizontal';

import ApiKey from '../../../models/apiKeyModel';

import {Loader, MenuPlacement, OptionsMenu, OptionsMenuItem} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import classNames from 'classnames';

import {AccessKey, ApiKeyStatus} from '../../../util/interfaces';

import useRight from '../../misc/useRight';

import InfoBanner from '../../misc/infoBanner';

interface Props {
    onClickAdd: MouseEventHandler;
    onClickActivate: (apiKey: ApiKey) => MouseEventHandler;
    onDeactivateKey: (apiKey: ApiKey) => MouseEventHandler;
    onRenew: MouseEventHandler;
    apiKeys: ApiKey[];
    loading: boolean;
}

export default function ApiSettings(props: Props) {
    const {t} = useTranslation();
    const [canSetup, canDeactivate] = useRight(AccessKey.API_SETTINGS_SETUP, AccessKey.API_SETTINGS_DISCONNECT);
    const {apiKeys, onClickActivate, onClickAdd, loading, onRenew, onDeactivateKey} = props;
    const hasOAuthKey = apiKeys.find((apiKey: ApiKey) => apiKey.useOAuth) !== undefined;

    function getOptions(apiKey: ApiKey): OptionsMenuItem[] {
        const options: OptionsMenuItem[] = [];

        if (apiKey.useOAuth) {
            const canRenew =
                [ApiKeyStatus.INACTIVE, ApiKeyStatus.ERROR].includes(apiKey.status) ||
                !apiKey.refreshExpiresAt ||
                IDate.now().add(1, 'month').isAfter(apiKey.refreshExpiresAt);

            if (canRenew && canSetup) {
                options.push({label: t('settings.api.reconnect', 'Opnieuw koppelen'), onClick: onRenew});
            }
        } else {
            if (canSetup) {
                options.push({label: t('settings.api.modify', 'Wijzigen'), onClick: onRenew});
                if (apiKey.status === ApiKeyStatus.INACTIVE) {
                    options.push({label: t('settings.api.activate', 'Activeren'), onClick: onClickActivate(apiKey)});
                }
            }
        }

        if (![ApiKeyStatus.INACTIVE, ApiKeyStatus.ERROR].includes(apiKey.status) && canDeactivate) {
            options.push({
                label: t('settings.api.disconnect', 'Koppeling verbreken'),
                onClick: onDeactivateKey(apiKey)
            });
        }

        return options;
    }

    function renderApiKey(apiKey: ApiKey) {
        const optionsRef = createRef<OptionsMenu>();

        function onClickRow() {
            optionsRef.current?.onOpenExternal();
        }

        return (
            <div key={apiKey.id} className={classNames('row', hasOAuthKey && 'oauth')} onClick={onClickRow}>
                <label>{apiKey.label}</label>
                {hasOAuthKey &&
                    (apiKey.useOAuth ? <label>{apiKey.refreshExpiresAt?.format('DD MMMM YYYY')}</label> : <label />)}
                <label>{apiKey.sellerID || '-'}</label>
                <label className={classNames('status', apiKey.status)}>{apiKey.translatedStatus}</label>
                <OptionsMenu
                    options={getOptions(apiKey)}
                    placement={MenuPlacement.BOTTOM_LEFT}
                    optionsComponent={<Icon icon={optionsHorizontal} className='options-icon' />}
                    containerClass='key-options'
                    ref={optionsRef}
                />
            </div>
        );
    }

    return (
        <div className='api-settings'>
            <InfoBanner
                featureKey='api_settings'
                variant='info'
                title={t('settings.api.bannerTitle', 'Koppeling met Bol')}
                showForNewUsersOnly={true}
            >
                {t('settings.api.bannerBody', 'Beheer jouw koppeling tussen bol en Bluemate.')}
            </InfoBanner>

            <Loader active={loading} />

            <div className='key-list'>
                <label className='list-title'>{t('settings.api.listTitle', 'Koppeling met Bol (Retailer API)')}</label>

                <div className={classNames('row', 'head', hasOAuthKey && 'oauth')}>
                    <label>{t('settings.api.colName', 'NAAM')}</label>
                    {hasOAuthKey && <label>{t('settings.api.colActiveUntil', 'ACTIEF TOT')}</label>}
                    <label>{t('settings.api.colSellerNumber', 'BOL VERKOOPNUMMER')}</label>
                    <label>{t('settings.api.colStatus', 'STATUS')}</label>
                    <label />
                </div>

                {apiKeys.map(renderApiKey)}

                {!apiKeys.length && (
                    <>
                        <label className='empty-label'>
                            {t('settings.api.emptyLabel', 'Je hebt geen API koppeling')}
                        </label>
                        <div className='row add'>
                            <label className='add-label' onClick={onClickAdd}>
                                {t('settings.api.setupApi', 'API koppeling instellen')}
                            </label>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
